/** @format */

import React, { FC, useState, useMemo } from 'react'
import { _$, Card, Dialog, Message, Editable, Button, Icon, Switch } from '@king-design/react'
import styled from 'styled-components'
import { MonitorSearch, TablePage, Divider } from 'src/components'
import { nameValidate } from 'src/utils/formValidate'
import useFormTable from 'src/hooks/useFormTable'
import useTableGroup from 'src/hooks/useTableGroup'
import API, { modifyShields, deleteShields, enableShields } from 'src/api/ApiAlarmSilences'
import { GetListSysEvents } from 'src/api/ApiAlarmServer'
import { useAsync } from 'react-use'
import EditDrawer from './EditDrawer'
import InstancesDialog from './InstancesDialog'

interface Props {
  className?: string
}
const MonitorAlarmSilences: FC<Props> = ({ className }) => {
  const productList = useTableGroup('productList')
  const [tableGroup, setTableGroup] = useState({
    ProductType: 999
  })
  const [query, setQuery] = useState<string>('')
  // 新建/编辑-Drawer
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false)
  const [editData, setEditData] = useState<any>()
  // 实例-Dialog
  const [dialogVisible, setDialogVisible] = useState<boolean>(false)
  const [dialogID, setDialogID] = useState<number>()

  // 获取事件列表
  const { value: eventList } = useAsync(async () => {
    const result = await GetListSysEvents({
      ProductType: 999
    })
    if (result && result?.code === 'Success' && result?.data.eventList) {
      const eventList = result.data.eventList
      return eventList
    } else {
      Message.error(result?.message)
      return null
    }
  }, [])

  const { run, result, total, pageState, loading }: any = useFormTable(
    API.getAlarmShields,
    {
      params: {
        ProductType: tableGroup.ProductType,
        ShieldName: query
      }
    },
    data => {
      return {
        tableData: data?.AlarmShields || [],
        totalCount: data?.TotalCount || 0
      }
    }
  )

  // 获取事件名称
  const getEventName = (productType, value) => {
    if (eventList?.length) {
      return (
        eventList.find(item => item.productType === productType && item.name === value)?.nameDesc ||
        ''
      )
    } else {
      return ''
    }
  }

  const scheme = useMemo(() => {
    if (productList?.length && eventList?.length) {
      return {
        ShieldName: {
          width: 200,
          fixed: 'left',
          title: _$('规则名称'),
          template: function (item) {
            return (
              <div className="policyType_cell">
                <Editable
                  value={item.ShieldName}
                  validate={value => nameValidate(value)}
                  onChangeValue={async value => {
                    const res = await modifyShields({
                      ...item,
                      ShieldName: value
                    })
                    if (res) {
                      run()
                    }
                  }}
                >
                  <p className="metric_legend">{item.ShieldName}</p>
                </Editable>
              </div>
            )
          }
        },
        State: {
          title: _$('状态'),
          width: 100,
          template: function (item) {
            if (item.Enabled === 2) {
              return (
                <p className="margin_text">
                  <Icon className="ion-close-circled" color="#999" />
                  {_$('未启用')}
                </p>
              )
            } else {
              return (
                <>
                  <p className="margin_text">
                    <Icon className="ion-checkmark-circled" color="success" />
                    {_$('启用中')}
                  </p>
                  {item.State === 1 ? (
                    <p className="margin_text" style={{ color: '#36b342' }}>
                      {_$('生效中')}
                    </p>
                  ) : (
                    <p className="margin_text" style={{ color: '#999' }}>
                      {_$('已失效')}
                    </p>
                  )}
                </>
              )
            }
          }
        },
        ProductType: {
          title: _$('产品类型'),
          width: 150,
          group: productList,
          template: function (item) {
            const productName =
              productList?.find(({ value }) => value === item.ProductType)?.label || ''
            return <span>{productName}</span>
          }
        },
        ShieldMetrics: {
          title: _$('屏蔽条件'),
          width: 350,
          template: function ({
            IsThresholdFlag,
            ShieldMetrics,
            IsEventFlag,
            ShieldEvents,
            ProductType
          }) {
            return (
              <>
                {IsThresholdFlag === 1 ? (
                  <p className="text_margin">
                    <b>{_$('指标')}：</b>
                    {ShieldMetrics?.length
                      ? ShieldMetrics.map(({ MetricDesc, MetricTags }) => {
                          if (MetricTags?.length) {
                            return MetricTags.map(tag => `${MetricDesc}[${tag}]`)?.join('、')
                          }
                          return MetricDesc
                        })?.join('、')
                      : _$('全部指标')}
                  </p>
                ) : null}
                {IsEventFlag === 1 ? (
                  <p className="text_margin">
                    <b>{_$('事件')}：</b>
                    <span>
                      {ShieldEvents?.length
                        ? ShieldEvents.map(({ EventName, EventTags }) => {
                            const name = getEventName(ProductType, EventName)
                            if (EventTags?.length) {
                              return EventTags.map(
                                tag =>
                                  `${name}[${tag.TagName}${tag.TagValue ? `=${tag.TagValue}` : ''}]`
                              )?.join('、')
                            }
                            return name
                          })?.join('、')
                        : _$('全部事件')}
                    </span>
                  </p>
                ) : null}
              </>
            )
          }
        },
        InstanceCount: {
          width: 100,
          title: _$('绑定实例'),
          template: function (item) {
            const { InstanceCount, ShieldID } = item
            const total = InstanceCount
            return total > 0 ? (
              <a
                onClick={() => {
                  setDialogID(ShieldID)
                  setDialogVisible(true)
                }}
              >
                {InstanceCount}
              </a>
            ) : (
              0
            )
          }
        },
        ShieldDescription: {
          width: 200,
          title: _$('规则描述')
        },
        ShieldTimeType: {
          width: 200,
          title: _$('生效时段'),
          template: ({
            ShieldTimeType,
            EffectEndDate,
            EffectEndTime,
            EffectStartDate,
            EffectStartTime
          }) => {
            if (ShieldTimeType === 1) {
              return <span>{_$('永久屏蔽')}</span>
            } else if (ShieldTimeType === 2) {
              return (
                <>
                  <p className="text_margin">{_$('绝对时间范围')}：</p>
                  <p className="text_margin">{`${EffectStartTime} - ${EffectEndTime}`}</p>
                </>
              )
            } else if (ShieldTimeType === 3) {
              return (
                <>
                  <p className="text_margin">
                    {_$('循环日期')}：
                    {EffectStartDate && EffectEndDate
                      ? `${EffectStartDate}-${EffectEndDate}`
                      : _$('永久')}
                  </p>
                  <p className="text_margin">
                    {_$('时间范围')}：{`${EffectStartTime}-${EffectEndTime}`}
                  </p>
                </>
              )
            }
            return ''
          }
        },
        Enabled: {
          title: _$('是否启用'),
          width: 100,
          template: ({ ShieldID, Enabled }) => {
            return (
              <Switch
                on={_$('启用')}
                off={_$('禁用')}
                width="62"
                value={Enabled === 1}
                beforeChange={async (from, to) => {
                  const result = await enableShields(Enabled, ShieldID)
                  if (result) {
                    Message.success(to ? _$('启用成功') : _$('禁用成功'))
                    return true
                  }
                  return false
                }}
                onChangeValue={async () => {
                  run()
                }}
              />
            )
          }
        },
        Action: {
          title: _$('操作'),
          width: 150,
          fixed: 'right',
          template: function (item) {
            const { ShieldID, ShieldName } = item
            return (
              <div className="ksc-space">
                <a
                  onClick={() => {
                    setEditData(item)
                    setDrawerVisible(true)
                  }}
                >
                  {_$('编辑')}
                </a>
                <Divider />
                {/* 删除 */}
                <a
                  onClick={() => {
                    Dialog.confirm({
                      title: ShieldName,
                      content: _$('确定删除所选规则?')
                    }).then(
                      async () => {
                        await deleteShields(ShieldID)
                        run({ PageIndex: 1 })
                      },
                      // eslint-disable-next-line @typescript-eslint/no-empty-function
                      () => {}
                    )
                  }}
                >
                  {_$('删除')}
                </a>
              </div>
            )
          }
        }
      }
    } else {
      return undefined
    }
  }, [productList, eventList])

  return (
    <div className={className}>
      <Card>
        <div className="header">
          <Button
            type="primary"
            onClick={() => {
              setDrawerVisible(true)
            }}
          >
            <Icon className="ion-plus-round"></Icon>
            {_$('新建屏蔽规则')}
          </Button>
          <div className="query-box">
            <MonitorSearch
              placeholder={_$('请输入规则名称')}
              value={query}
              onChange={value => {
                setQuery(value)
                run({
                  PageIndex: 1,
                  ProductType: tableGroup.ProductType,
                  ShieldName: value
                })
              }}
            />
          </div>
        </div>
        <div className="marginTop">
          {scheme ? (
            <TablePage
              showPage={true}
              tableProps={{
                rowKey: () => 'ShieldID',
                scheme: scheme,
                data: result,
                checkType: 'none',
                resizable: true,
                group: tableGroup,
                onChangeGroup: value => {
                  setTableGroup(value)
                  run({
                    PageIndex: 1,
                    ProductType: value.ProductType,
                    TemplateName: query
                  })
                }
              }}
              run={run}
              loading={loading}
              pageProps={{ total, ...pageState }}
            />
          ) : null}
        </div>
      </Card>
      {/* 新建/编辑 */}
      <EditDrawer
        visible={drawerVisible}
        editData={editData}
        eventList={eventList}
        onClose={update => {
          setEditData(undefined)
          setDrawerVisible(false)
          if (update) {
            run()
          }
        }}
      />
      {/* 实例 */}
      <InstancesDialog
        visible={dialogVisible}
        ShieldID={dialogID}
        onClose={() => {
          setDialogID(undefined)
          setDialogVisible(false)
        }}
      />
    </div>
  )
}

export default styled(MonitorAlarmSilences)`
  .header {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  .policyType_cell {
    display: flex;
    align-items: center;
    .k-input {
      width: 100%;
    }
    .k-editable {
      .c-ellipsis {
        white-space: normal;
      }
    }
    .metric_legend {
      max-width: 100%;
      margin: 0;
      word-break: break-all;
    }
  }
  .trigger_rules {
    h5 {
      font-size: 12px;
    }
  }
  .margin_text {
    margin: 0;
    display: flex;
    align-items: center;
    .k-icon {
      margin-right: 4px;
    }
  }
`
